import classNames from "classnames";
import { useEffect, useState } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";

import { showMask } from "shared/actions/slideDetailActions";
import { lesionLineTypeByName } from "shared/reducers/aiReducer";

import { useHasAILoaded } from "hooks/aiHooks";
import { selectSlideClassification } from "selectors/classificationSelector";
import ApplicationState from "store/ApplicationState";
import { useAppSelector } from "store/hooks";
import { segregratedClassificationLines } from "util/aiUtils";

import { DialogEnum } from "./dialogs/Dialog";
import DialogButton from "./dialogs/DialogButton";

export interface AIButtonProps {
  currentDialog: DialogEnum | null;
  setDialog: Function;
  className?: string;
}

/*
When only one single positive diagnosis exists:
  Automatically select that positive line out of the overlay.
  Title of the button should become the short name of the overlay, e.g. "AI: MEL Overlay".
  When the overlay is on, the button should be colored purple.

When multiple positive lesion lines are present: default the overlay OFF.
  Give it the text of "AI: Multiple Positive".
 */
const AIButton = (props: AIButtonProps) => {
  const slideDetail = useSelector((app: ApplicationState) => app.slideDetail);
  const { currentDialog, setDialog } = props;

  const maskLesionLineType = useAppSelector((app) => app.ai.maskLesionLineType);

  const ai = useAppSelector((state) =>
    selectSlideClassification(state, slideDetail.id)
  );
  const loadedPositives =
    ai?.lesion_lines?.filter((ll) => ll.diagnosis_result === "POSITIVE") || [];

  const dispatch = useDispatch();

  const [revealResult, setRevealResult] = useState(false);
  const linesByType = loadedPositives.map((l) =>
    lesionLineTypeByName(l.diagnosis_name)
  );
  const indexOfLine = maskLesionLineType
    ? linesByType.indexOf(maskLesionLineType)
    : -1;

  useEffect(() => {
    // If the user opens the Dialog or is looking at any line,
    // ... we can assume it's OK to 'reveal results'
    if (currentDialog === DialogEnum.AIOverlay || maskLesionLineType)
      setRevealResult(true);
  }, [currentDialog, maskLesionLineType]);

  useHotkeys(
    "a, shift+a",
    () => {
      // Case: Only One Positive Line
      if (loadedPositives.length === 1) {
        if (maskLesionLineType) {
          // Toggle 'off' if it's already 'on'
          dispatch(showMask(undefined));
        } else {
          dispatch(
            showMask(lesionLineTypeByName(loadedPositives[0].diagnosis_name))
          );
        }
      } else if (loadedPositives.length > 1) {
        if (maskLesionLineType) {
          if (indexOfLine === linesByType.length - 1 || indexOfLine < 0) {
            // Toggle 'off' if we're on the last positive line
            // _or_ on a non-positive line
            dispatch(showMask(undefined));
          } else {
            dispatch(
              showMask(
                lesionLineTypeByName(
                  loadedPositives[indexOfLine + 1].diagnosis_name
                )
              )
            );
          }
        } else {
          // Kick off the first line, but only after it's been pressed once
          if (revealResult) {
            dispatch(
              showMask(lesionLineTypeByName(loadedPositives[0].diagnosis_name))
            );
          } else {
            setRevealResult(true);
          }
        }
      } else {
        // No positive lines: Toggle the button label
        setRevealResult(true);
        if (maskLesionLineType) {
          dispatch(showMask(undefined));
        }
      }
    },
    [
      loadedPositives,
      maskLesionLineType,
      revealResult,
      linesByType,
      indexOfLine,
    ]
  );

  useHotkeys(
    "y, shift+y",
    () => {
      if (currentDialog === DialogEnum.AIOverlay) {
        setDialog(null);
      } else {
        setDialog(DialogEnum.AIOverlay);
      }
    },
    [setDialog]
  );

  const aiLoaded = useHasAILoaded(slideDetail);

  useEffect(() => {
    // If you switch to this slide from a slide that has
    // ... a line this one doesn't, put me on the highest
    // ... positive line, or turn me off
    if (ai?.id) {
      const { positiveLesionLineTypes, notRunLesionLineTypes } =
        segregratedClassificationLines(ai);

      if (
        maskLesionLineType &&
        notRunLesionLineTypes.includes(maskLesionLineType)
      ) {
        if (positiveLesionLineTypes?.length) {
          dispatch(showMask(positiveLesionLineTypes.at(0)));
        } else {
          dispatch(showMask(undefined));
        }
      }
    }
  }, [maskLesionLineType, ai?.id]);

  const buttonLabel = !aiLoaded ? (
    "Loading..."
  ) : maskLesionLineType ? (
    <>
      {maskLesionLineType} Overla<u>y</u>
      {loadedPositives.length > 1 && (
        <>
          {" "}
          {indexOfLine > -1 && (
            <span>
              ({indexOfLine + 1}/{loadedPositives.length})
            </span>
          )}
        </>
      )}
    </>
  ) : loadedPositives.length > 1 && revealResult ? (
    "Multiple Positive"
  ) : loadedPositives.length < 1 && revealResult ? (
    "No Positive Lines"
  ) : (
    <>
      No Overla<u>y</u>
    </>
  );

  return (
    <DialogButton
      currentDialog={currentDialog}
      wantedDialog={DialogEnum.AIOverlay}
      setDialog={setDialog}
      hotkey="y, shift+y"
      title={`AI Overlay ${maskLesionLineType ?? "Off"}`}
      className={classNames(maskLesionLineType ? "DialogButton--AI-2" : "", {
        active: currentDialog === DialogEnum.AIOverlay,
      })}
    >
      <u>A</u>I: {buttonLabel}
    </DialogButton>
  );
};

export default AIButton;
